import React, { useState } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    TableRow,
    TableCell,
    Typography,
    Chip,
    Box,
    Tooltip,
    Menu,
    MenuItem,
    IconButton
} from "@mui/material";
import {
    isMbxUserVerifiedStaff,
    mbxUserAtom,
    updateBookingInVisibleBookingsAtom,
    updateOrderNotesInVisibleBookingsAtom
} from "../../atoms/atoms";
import {
    selectedBookingAtom,
    selectedBookingIdAtom,
    selectedTabAtom
} from "../../atoms/bookingsTable";
import { ClientCard } from "./ClientCard";
import { ProviderCard } from "./ProviderCard";
import { VMDialog } from "../VMDialog";
import { graphql, graphqlOperation } from "../../modules/AmplifyServices";
import * as queries from "../../graphql/queries";
import { useTheme } from "@mui/material/styles";
import CurrencyFormat from "react-currency-format";
import { getCurrencySymbol } from "../../utils";
import ChatIcon from "@mui/icons-material/Chat";
import LoopIcon from "@mui/icons-material/Loop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PackageIcon from "../../utils/CommonComponents/PackageIcon";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BookingNotesDialog from "../../components/BookingNotesDialog";
import moment from "moment-timezone";

export const BookingRow = ({
    bookingData: {
        id,
        serviceName,
        location,
        locationTimezone,
        companyLocationName,
        isVirtual,
        orderType,
        orderId,
        cancelledByRole = "",
        orderNotes,
        bookingNotes,
        rrule = "",
        client: {
            clientId,
            clientUserId,
            isClientActive,
            clientFirstName,
            clientLastName,
            clientEmail,
            clientAddress,
            clientPhoneNumber,
            clientHomePhone,
            clientNotes,
            clientDefaultPartialCC,
            clientRegistered,
            userRole
        },
        packageInitialQuantity = "",
        provider: {
            vmlink,
            providerId,
            userId,
            providerFirstName,
            providerLastName,
            providerTimezone
        },
        providerDateAndTime = Date.now(),
        status,
        total,
        totalAddChg
    }
}) => {
    console.log("Bana isClientActive in booking row: ", isClientActive);
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setSelectedBookingId] = useAtom(selectedBookingIdAtom);
    const [selectedBooking] = useAtom(selectedBookingAtom);
    const [selectedTab] = useAtom(selectedTabAtom);

    const [, updateBookingInVisibleBookings] = useAtom(
        updateBookingInVisibleBookingsAtom
    );

    const [, updateOrderNotesInVisibleBookings] = useAtom(
        updateOrderNotesInVisibleBookingsAtom
    );

    const [tooltipTitle, setTooltipTitle] = useState("");

    //Virtual Link States
    const [openVMDialog, setOpenVMDialog] = useState(false);
    const [serviceProv, setServiceProv] = useState("");
    const [startDate, setStartDate] = useState("");
    const [serviceLoc, setServiceLoc] = useState("");
    const [showNoteDialog, setShowNoteDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);

    const getChipColor = (status) => {
        switch (status) {
            case "SCHEDULED":
                return theme.palette.info;
            case "COMPLETED":
                return theme.palette.success;
            case "ARRIVED":
                return theme.palette.success;
            case "CANCELLED":
                return "disabled";
            case "REQUESTED":
                return theme.palette.info;
            case "NOSHOW":
                return theme.palette.caution;
            default:
                return "default";
        }
    };

    let parsedClientNotes = "";

    // Function to check if the string is valid JSON (object or array)
    const isValidJson = (str) => {
        try {
            const parsed = JSON.parse(str);
            return (
                parsed && (typeof parsed === "object" || Array.isArray(parsed))
            ); // Only valid if it's an object or array
        } catch (e) {
            return false;
        }
    };

    if (clientNotes && clientNotes.trim() !== "") {
        if (isValidJson(clientNotes)) {
            parsedClientNotes = JSON.parse(clientNotes);
        } else {
            parsedClientNotes = clientNotes;
        }
    } else {
        parsedClientNotes = "";
    }

    // Handle opening the menu
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle closing the menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Handle menu item clicks
    const handleMenuItemClick = () => {
        handleClose();
    };

    const handleSaveNotes = (applyNotesToOrder, orderOrBookingId, newNotes) => {
        let updatedBookingData = {};

        if (applyNotesToOrder) {
            updatedBookingData = {
                id: id,
                order: {
                    ...selectedBooking.order,
                    Notes: newNotes
                },
                Notes: null
            };
            const updatedOrderData = {
                order: {
                    ...selectedBooking.order,
                    Notes: newNotes
                },
                Notes: null
            };

            updateOrderNotesInVisibleBookings({
                orderId: selectedBooking.order.id,
                updatedOrderData
            });
        } else {
            updatedBookingData = {
                id: id,
                Notes: newNotes
            };
        }

        updateBookingInVisibleBookings(updatedBookingData);
    };

    const locationTimezoneAbbr = moment.tz(locationTimezone).zoneName(); // Get the timezone abbreviation for location
    const providerTimezoneAbbr = moment.tz(providerTimezone).zoneName(); // Get the timezone abbreviation for provider

    const getLocationDateAndTime = () => {
        const providerDateTime = `${providerDateAndTime.formattedDateWithYear} ${providerDateAndTime.formattedTime}`;

        const providerMoment = moment.tz(
            providerDateTime,
            "ddd, MMM D, YYYY h:mm A",
            providerTimezone
        );

        const locationMoment = providerMoment.clone().tz(locationTimezone);

        // Format the result in the desired format
        return {
            formattedDateWithYear: locationMoment.format("ddd, MMM D, YYYY"),
            formattedDateWithoutYear: locationMoment.format("ddd, MMM D"),
            formattedTime: locationMoment.format("h:mm A"),
            timezone: locationMoment.format("z") // This will return the abbreviation like PST, PDT, etc.
        };
    };

    // Recurrance Description
    const fetchRecurringInfo = async () => {
        if (orderType === "ONGOING") {
            try {
                //First, fetch the repeatApptCreationRecord
                let result = await graphql(
                    graphqlOperation(queries.getRepeatApptCreation, {
                        id: mbxUser.companyId,
                        datakey: `O-${orderId}`
                    })
                );

                let repeatApptRecord = result.data.getRepeatApptCreation;
                repeatApptRecord.extented = JSON.parse(
                    result.data.getRepeatApptCreation.extented
                );

                //Second, check for an rrule, if there is none then construct one
                //an rrule will exist for any repeatApptCreation record created after MBX-36 was released... unless there was an error ofcourse
                let rruleString;
                if (!repeatApptRecord?.extented?.rrule) {
                    rruleString = await constructRRuleFromRepeatApptCreation(
                        repeatApptRecord,
                        providerDateAndTime
                    );
                } else {
                    rruleString = await getRelevantRRule(
                        providerDateAndTime,
                        repeatApptRecord.extented.rrule
                    );
                }

                //Convert the rruleString into a "user-friendly", easy to read label
                const rruleLabel = constructRruleLabel(
                    rruleString,
                    providerDateAndTime
                );
                setTooltipTitle(rruleLabel);
            } catch (e) {
                console.log(" Error fetching recurringApptCreation record", e);
            }
        }
    };

    async function constructRRuleFromRepeatApptCreation(
        repeatApptRecord,
        bookingStartDate
    ) {
        const repeatCount = repeatApptRecord.repeatCount;
        let repeatUnit = repeatApptRecord.repeatUnit;
        const daysOfWeek = repeatApptRecord.extented.daysOfWeek;

        let selectedDays;
        const days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
        if (daysOfWeek && daysOfWeek.length > 0) {
            selectedDays = days.filter((day, index) => daysOfWeek[index]);
        } else {
            let dateString = bookingStartDate;
            const date = moment(dateString, "YYYY-MM-DD");
            const dayOfWeek = date.day();
            selectedDays = [days[dayOfWeek]];
        }
        if (repeatUnit === "weeks") {
            repeatUnit = "WEEKLY";
        } else if (repeatUnit === "months") {
            repeatUnit = "MONTHLY";
        } else if (repeatUnit === "years") {
            repeatUnit = "YEARLY";
        }
        // Construct the RRULE string
        let rruleString = `FREQ=${repeatUnit.toUpperCase()};`;
        if (repeatCount) {
            rruleString += `INTERVAL=${repeatCount};`;
        }

        if (selectedDays.length > 0) {
            rruleString += `BYDAY=${selectedDays.join(",")}`;
        }

        return rruleString;
    }

    function constructRruleLabel(rruleString) {
        try {
            // Parse the rruleString into an object
            const rruleObject = parseRruleString(rruleString);

            // Extract relevant information
            const freq = rruleObject.FREQ || "";
            const interval = rruleObject.INTERVAL || 1;
            const byDay = rruleObject.BYDAY || "";
            let until = rruleObject.UNTIL || "";
            const xOccur = rruleObject.XOCCUR || "";

            const inputDate = new Date(
                providerDateAndTime.formattedDateWithYear
            );

            const options = {
                month: "short",
                day: "2-digit",
                year: "numeric"
            };
            const startingDate = inputDate.toLocaleDateString("en-US", options);
            if (until) {
                until = new Date(`${until}T00:00:00`).toLocaleDateString(
                    "en-US",
                    options
                );
            }
            // Build the user-friendly label
            let label = `Recurring`;

            // Frequency
            if (freq === "WEEKLY") {
                if (interval > 1) {
                    label += ` every ${interval} weeks`;
                } else {
                    label += ` weekly`;
                }
            } else if (freq === "MONTHLY") {
                if (interval > 1) {
                    label += ` every ${interval} months`;
                } else {
                    label += ` monthly`;
                }
            } else if (freq === "YEARLY") {
                if (interval > 1) {
                    label += ` every ${interval} years`;
                } else {
                    label += ` yearly`;
                }
            }

            // By day
            if (byDay && freq === "WEEKLY") {
                label += ` on ${formatByDay(byDay)}`;
            } else if (byDay === "NUMBER" && freq === "MONTHLY") {
                const dayOfMonth = startingDate.getDate();
                label += ` on day ${dayOfMonth}`;
            } else if (byDay === "WEEK" && freq === "MONTHLY") {
                const dayOfWeek = startingDate.toLocaleDateString("en-US", {
                    weekday: "short"
                });
                const weekOccurence =
                    Math.floor((inputDate.getDate() - 1) / 7) + 1;
                const occurenceLabels = [
                    "first",
                    "second",
                    "third",
                    "fourth",
                    "last"
                ];

                label += ` on every ${
                    occurenceLabels[weekOccurence - 1]
                } ${dayOfWeek}`;
            } else if (freq === "YEARLY") {
                const options = { month: "short", day: "2-digit" };
                const dayOfMonth = inputDate.toLocaleDateString(
                    "en-US",
                    options
                );
                label += ` on ${dayOfMonth}`;
            }

            // Number of sessions and until
            if (xOccur) {
                label += ` for a total of ${xOccur} sessions`;
            } else if (until) {
                label += `, until ${until}`;
            }

            setTooltipTitle(label);
            return label;
        } catch (e) {
            console.log("error constructing rrule label", e);
        }
    }

    async function getRelevantRRule(bookingDate, rruleList) {
        for (const rrule of rruleList) {
            const untilMatch = /UNTIL=([0-9-]+)|$/.exec(rrule);
            const untilDate = untilMatch ? untilMatch[1] : null;
            if (
                !untilDate ||
                new Date(untilDate) >=
                    new Date(bookingDate.formattedDateWithYear)
            ) {
                return rrule;
            }
        }

        // Return null if no matching rule is found
        return null;
    }

    // Helper function to parse rruleString into an object
    function parseRruleString(rruleString) {
        const rruleObject = {};
        const parts = rruleString.split(";");
        parts.forEach((part) => {
            const [key, value] = part.split("=");
            rruleObject[key] = value;
        });
        return rruleObject;
    }

    // Helper function to format BYDAY field
    function formatByDay(byDay) {
        const days = byDay.split(",");
        const formattedDays = days.map(getFullDayString);
        const allButLast = formattedDays.slice(0, -1).join(", ");

        return formattedDays.length === 1
            ? formattedDays[0]
            : `${allButLast} and ${formattedDays[formattedDays.length - 1]}`;
    }

    function getFullDayString(day) {
        const dayMap = {
            MO: "Mon",
            TU: "Tue",
            WE: "Wed",
            TH: "Thu",
            FR: "Fri",
            SA: "Sat",
            SU: "Sun"
        };

        return dayMap[day] || day; // If the day is not found in the map, return the original value
    }

    const getTooltipTitle = async () => {
        let title = "";
        if (orderType.toUpperCase() === "PACKAGE") {
            if (packageInitialQuantity !== "") {
                setTooltipTitle(
                    `Package of ${packageInitialQuantity} sessions`
                );
            } else {
                try {
                    let result = await graphql(
                        graphqlOperation(queries.getOrder, {
                            id: orderId
                        })
                    );
                    title = `Package of ${result?.data?.getOrder?.clientpackage?.initialQuantity} sessions`;
                    setTooltipTitle(title);
                } catch (e) {
                    console.log("Error fetching client package", e);
                }
            }
        } else setTooltipTitle("Single booking");
    };
    // Determine the icon to display based on orderType
    let IconComponent;

    if (orderType.toUpperCase() === "SINGLE") {
        IconComponent = CheckCircleOutlineIcon;
    } else if (orderType.toUpperCase() === "PACKAGE") {
        IconComponent = PackageIcon;
    } else if (
        orderType.toUpperCase() === "ONGOING" ||
        orderType.toUpperCase() === "FOREVER"
    ) {
        IconComponent = LoopIcon;
    }

    return (
        <TableRow
            key={id}
            variant={
                status === "CANCELLED"
                    ? "cancelled"
                    : status ===
                      ("REQUESTED" || "BROADCASTED" || "BROADCAST_SCHEDULED")
                    ? "pending"
                    : null
            }
        >
            <TableCell
                sx={{
                    width: "1%"
                }}
            >
                <Tooltip title={tooltipTitle}>
                    <Box
                        onMouseEnter={
                            orderType.toUpperCase() === "ONGOING"
                                ? fetchRecurringInfo
                                : orderType.toUpperCase() === "FOREVER"
                                ? setTooltipTitle(
                                      constructRruleLabel(
                                          rrule,
                                          providerDateAndTime
                                      )
                                  )
                                : getTooltipTitle
                        }
                    >
                        <IconComponent
                            stroke={
                                IconComponent === PackageIcon
                                    ? theme.palette.primary.main
                                    : "none"
                            }
                            sx={{
                                color: theme.palette.primary.main,
                                height:
                                    IconComponent === CheckCircleOutlineIcon
                                        ? "23px"
                                        : "26px",
                                width:
                                    IconComponent === CheckCircleOutlineIcon
                                        ? "23px"
                                        : "26px",
                                transform:
                                    IconComponent === LoopIcon
                                        ? "rotate(137deg)"
                                        : ""
                            }}
                        />
                    </Box>
                </Tooltip>
            </TableCell>
            <TableCell
                sx={{
                    width: "25%",
                    minWidth: "200px",
                    whiteSpace: "normal"
                }}
            >
                {serviceName.length > 80 ? (
                    <Tooltip title={serviceName}>
                        <Typography sx={{ display: "inline-block" }}>
                            {`${serviceName.substring(0, 80)}...`}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography>{serviceName}</Typography>
                )}
            </TableCell>
            <TableCell
                sx={{
                    width: "22%",
                    whiteSpace: "normal",
                    pointerEvents: "auto !important"
                }}
            >
                {companyLocationName ? (
                    companyLocationName.length > 65 ? (
                        <Tooltip title={companyLocationName}>
                            <Typography
                                sx={{
                                    display: "inline-block"
                                }}
                            >
                                {`${companyLocationName.substring(0, 65)}...`}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Company location">
                            <Typography>{companyLocationName}</Typography>
                        </Tooltip>
                    )
                ) : isVirtual ? (
                    selectedTab === "past" ||
                    status === "CANCELLED" ||
                    status === "REQUESTED" ||
                    status === "BROADCASTED" ||
                    status === "BROADCAST_SCHEDULED" ? (
                        <Typography
                            variant="text"
                            sx={{ pointerEvents: "none !important" }}
                        >
                            {location}
                        </Typography>
                    ) : vmlink ? (
                        <Tooltip title="Join meeting">
                            <Typography
                                variant={"link"}
                                onClick={() => {
                                    setServiceProv(
                                        `${providerFirstName} ${providerLastName}`
                                    );
                                    setStartDate(
                                        `${providerDateAndTime.formattedDateWithoutYear}, at ${providerDateAndTime.formattedTime} (${providerDateAndTime.timezone})`
                                    );
                                    setServiceLoc(vmlink);
                                    setOpenVMDialog(true);
                                }}
                            >
                                {location}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={
                                isVerifiedStaff
                                    ? "The provider has not added a virtual meeting link"
                                    : "You have not added a virtual meeting link – to add one, click on the Profile tab"
                            }
                        >
                            <Typography
                                variant="text"
                                onClick={() => {
                                    setServiceProv(
                                        `${providerFirstName} ${providerLastName}`
                                    );
                                    setStartDate(
                                        `${providerDateAndTime.formattedDateWithoutYear}, at ${providerDateAndTime.formattedTime} (${providerDateAndTime.timezone})`
                                    );
                                }}
                            >
                                {location}
                            </Typography>
                        </Tooltip>
                    )
                ) : location.length > 65 ? (
                    <Tooltip title={location}>
                        <Typography
                            sx={{
                                "&.MuiTypography-link": {
                                    color:
                                        status === "CANCELLED"
                                            ? theme.palette.disabled
                                            : `${theme.palette.primary.main} !important`
                                }
                            }}
                            variant="link"
                            onClick={() => {
                                window.open(
                                    `https://www.google.com/maps/search/?api=1&query=${location.replaceAll(
                                        " ",
                                        "+"
                                    )}`,
                                    "_blank"
                                );
                            }}
                        >
                            {`${location.substring(0, 65)}...`}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography
                        sx={{
                            "&.MuiTypography-link": {
                                color:
                                    status === "CANCELLED"
                                        ? theme.palette.disabled
                                        : `${theme.palette.primary.main} !important`
                            }
                        }}
                        variant="link"
                        onClick={() => {
                            window.open(
                                `https://www.google.com/maps/search/?api=1&query=${location.replaceAll(
                                    " ",
                                    "+"
                                )}`,
                                "_blank"
                            );
                        }}
                    >
                        {location}
                    </Typography>
                )}
            </TableCell>
            <VMDialog
                openVMDialog={openVMDialog}
                onClose={() => {
                    setOpenVMDialog(false);
                }}
                inProgress={status === "ARRIVED"}
                serviceProv={serviceProv}
                clientName={`${clientFirstName} ${clientLastName}`}
                serviceLoc={serviceLoc}
                startDate={startDate}
            />
            <TableCell
                sx={{
                    width: "15%"
                }}
            >
                <ClientCard
                    {...{
                        bookingId: id,
                        clientId,
                        clientUserId,
                        isClientActive,
                        combinedName: `${clientFirstName} ${clientLastName}`,
                        clientFirstName,
                        clientLastName,
                        clientEmail,
                        clientAddress,
                        clientPhoneNumber,
                        clientHomePhone,
                        clientNotes: parsedClientNotes,
                        clientDefaultPartialCC,
                        clientRegistered,
                        clientUserRole: userRole
                    }}
                />
            </TableCell>
            {isVerifiedStaff && (
                <TableCell
                    sx={{
                        width: "10%"
                    }}
                >
                    <ProviderCard
                        {...{
                            bookingId: id,
                            providerId,
                            userId,
                            combinedName: `${providerFirstName} ${providerLastName}`
                        }}
                    />
                </TableCell>
            )}
            <TableCell
                sx={{
                    width: "10%"
                }}
            >
                <Typography>
                    {providerDateAndTime.formattedDateWithYear}
                </Typography>
                <Typography>
                    {providerDateAndTime.formattedTime} (
                    {providerDateAndTime.timezone})
                </Typography>
                {locationTimezoneAbbr !== providerTimezoneAbbr && (
                    <Typography variant="helperText" component="p">
                        {getLocationDateAndTime().formattedTime} (
                        {getLocationDateAndTime().timezone})
                    </Typography>
                )}
            </TableCell>
            <TableCell
                sx={{
                    textAlign: "center",
                    width: "5%"
                }}
            >
                <Tooltip
                    title={
                        bookingNotes && JSON.parse(bookingNotes)[0].notes
                            ? JSON.parse(bookingNotes)[0].notes
                            : !bookingNotes &&
                              orderNotes &&
                              JSON.parse(orderNotes)[0].notes
                            ? JSON.parse(orderNotes)[0].notes
                            : status ===
                              ("REQUESTED" ||
                                  "BROADCASTED" ||
                                  "BROADCAST_SCHEDULED")
                            ? ""
                            : "Add note"
                    }
                >
                    <ChatIcon
                        style={{
                            color:
                                status ===
                                ("REQUESTED" ||
                                    "BROADCASTED" ||
                                    "BROADCAST_SCHEDULED")
                                    ? theme.palette.grey.light
                                    : (bookingNotes &&
                                          JSON.parse(bookingNotes)[0].notes !==
                                              "") ||
                                      (!bookingNotes &&
                                          orderNotes &&
                                          JSON.parse(orderNotes)[0].notes !==
                                              "")
                                    ? theme.palette.primary.main
                                    : theme.palette.icon.disabled,
                            cursor:
                                status ===
                                ("REQUESTED" ||
                                    "BROADCASTED" ||
                                    "BROADCAST_SCHEDULED")
                                    ? "default"
                                    : "pointer"
                        }}
                        onClick={() => {
                            if (
                                status !==
                                ("REQUESTED" ||
                                    "BROADCASTED" ||
                                    "BROADCAST_SCHEDULED")
                            ) {
                                setSelectedBookingId(id);
                                setShowNoteDialog(true);
                            }
                        }}
                    />
                </Tooltip>
            </TableCell>
            <Dialog
                open={showNoteDialog}
                onClose={() => setShowNoteDialog(false)}
                fullWidth={true}
            >
                <BookingNotesDialog
                    onClose={() => setShowNoteDialog(false)}
                    noteDialogCreatedBy={providerFirstName}
                    noteDialogContent={
                        bookingNotes
                            ? JSON.parse(bookingNotes)[0]
                            : orderNotes
                            ? JSON.parse(orderNotes)[0]
                            : ""
                    }
                    rowBookingId={id}
                    noteClientName={`${clientFirstName} ${clientLastName}`}
                    noteServiceName={serviceName}
                    noteBookingStartDate={startDate}
                    noteOrderNumber={orderId}
                    onSaveNotes={handleSaveNotes}
                    loggedInUser={mbxUser}
                />
            </Dialog>
            <TableCell
                sx={{
                    textAlign: "center",
                    width: "15%",
                    pointerEvents: "auto"
                }}
            >
                {status === "CANCELLED" ? (
                    <Tooltip title={`Cancelled by ${cancelledByRole}`}>
                        <Chip
                            variant={"contained"}
                            sx={{
                                backgroundColor: getChipColor(status).light
                            }}
                            label={
                                <Box
                                    sx={{
                                        display: "flex",
                                        textAlign: "center"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: `${
                                                getChipColor(status).main
                                            } !important`
                                        }}
                                    >
                                        {status}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Tooltip>
                ) : (
                    <Chip
                        variant={"contained"}
                        sx={{
                            backgroundColor: getChipColor(status).light
                        }}
                        label={
                            <Box
                                sx={{
                                    display: "flex",
                                    textAlign: "center"
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: `${
                                            getChipColor(status).main
                                        } !important`
                                    }}
                                >
                                    {status === "NOSHOW"
                                        ? "NO-SHOW"
                                        : status === "REQUESTED"
                                        ? "PENDING"
                                        : status}
                                </Typography>
                            </Box>
                        }
                    />
                )}
            </TableCell>
            {isVerifiedStaff && (
                <TableCell
                    sx={{
                        textAlign: "right",
                        width: "5%"
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography>
                            <CurrencyFormat
                                value={total}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={getCurrencySymbol()}
                                decimalScale={2}
                                fixedDecimalScale={true}
                            />
                        </Typography>
                        {totalAddChg && (
                            <Typography>
                                <CurrencyFormat
                                    value={totalAddChg}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={`+ ${getCurrencySymbol()}`}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                />
                            </Typography>
                        )}
                    </Box>
                </TableCell>
            )}
            <TableCell
                sx={{
                    textAlign: "left",
                    paddingLeft: "0px !important",
                    width: "1%"
                }}
            >
                <Box>
                    <IconButton onClick={handleClick}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem
                            onClick={() => handleMenuItemClick("Arrived")}
                        >
                            Arrived
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuItemClick("Cancel")}>
                            Cancel
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuItemClick("Edit")}>
                            Edit
                        </MenuItem>
                    </Menu>
                </Box>
            </TableCell>
        </TableRow>
    );
};
